/**
 * NEXUMA — Role Permissions Service
 *
 * Persistencë për ndryshimet e privilegjeve të roleve dhe audit log.
 *
 * Burimet e të dhënave:
 *   • Backend: `/api/customRoles` (CRUD i plotë për role të krijuara nga UI)
 *   • localStorage: override-t e roleve sistemike (ROLES_CONFIG) që nuk janë në DB
 *   • localStorage: audit log
 *   • localStorage: per-user permission overrides
 *
 * Çelësat e localStorage-ës:
 *   nexuma:rbac:role_overrides   — { [roleKey]: { permissions: string[], label?: string } }
 *   nexuma:rbac:audit_log        — AuditEntry[]
 *   nexuma:rbac:user_overrides   — { [userId]: { add: string[], remove: string[] } }
 *
 * Shënim:
 *   Kjo lejon redaktim të role-ve sistemike (HR Manager, Admin, etj.) pa endpoint
 *   të dedikuar. Kur backend-i të mbështesë `PATCH /api/roles/:roleKey`, lidhe
 *   te `saveRolePermissions` dhe localStorage mund të hiqet.
 */

const LS_ROLE_OVERRIDES = 'nexuma:rbac:role_overrides';
const LS_AUDIT_LOG      = 'nexuma:rbac:audit_log';
const LS_USER_OVERRIDES = 'nexuma:rbac:user_overrides';
const MAX_AUDIT_ENTRIES = 200;

const API = process.env.REACT_APP_API_URL || '';
const SESSION_KEY = 'logedInUser';

/* ──────────────────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────────────────── */
function safeRead(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch { return fallback; }
}

function safeWrite(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); return true; }
  catch { return false; }
}

function getAuthHeaders() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
      || localStorage.getItem(SESSION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const token = parsed?.token || parsed?.data?.token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch { return {}; }
}

function getCurrentUserName() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
      || localStorage.getItem(SESSION_KEY);
    if (!raw) return 'Sistemi';
    const p = JSON.parse(raw);
    const u = p?.data || p || {};
    const name = [u.FirstName || u.first_name, u.LastName || u.last_name]
      .filter(Boolean).join(' ').trim();
    return name || u.email || u.name || 'Sistemi';
  } catch { return 'Sistemi'; }
}

/* ──────────────────────────────────────────────────────────────────────────
 * Role overrides (localStorage)
 * ────────────────────────────────────────────────────────────────────────── */
export function loadRoleOverrides() {
  return safeRead(LS_ROLE_OVERRIDES, {});
}

/**
 * Resolve effective permissions for a role:
 *   - If override exists in localStorage → use it (full replacement)
 *   - Otherwise → use the base list from ROLES_CONFIG
 */
export function resolveRolePermissions(roleKey, basePermissions = []) {
  const overrides = loadRoleOverrides();
  const ov = overrides[roleKey];
  if (ov?.permissions && Array.isArray(ov.permissions)) return ov.permissions;
  return basePermissions;
}

export function resolveRoleLabel(roleKey, baseLabel) {
  const overrides = loadRoleOverrides();
  return overrides[roleKey]?.label || baseLabel;
}

/**
 * Save role permissions (full list).
 * Returns { ok, savedAt }.
 */
export async function saveRolePermissions(roleKey, permissions, opts = {}) {
  const { roleLabel, diff } = opts;
  const perms = [...new Set(permissions)];
  // localStorage cache — render i menjëhershëm + fallback offline.
  const overrides = loadRoleOverrides();
  overrides[roleKey] = {
    ...(overrides[roleKey] || {}),
    permissions: perms,
    updatedAt: new Date().toISOString(),
  };
  safeWrite(LS_ROLE_OVERRIDES, overrides);

  // Backend = burimi i vërtetë: ruhet dhe ZBATOHET realisht (effectivePermissions),
  // dhe ndahet mes pajisjeve/përdoruesve. Nëse offline, cache-i lokal e mban.
  try {
    await fetch(`${API}/api/roleOverrides/${encodeURIComponent(roleKey)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ permissions: perms, label: roleLabel }),
    });
  } catch { /* offline → mbetet në cache */ }

  appendAudit({
    kind: 'role_permissions_saved',
    roleKey,
    roleLabel: roleLabel || roleKey,
    added: diff?.added || [],
    removed: diff?.removed || [],
  });

  return { ok: true, savedAt: overrides[roleKey].updatedAt };
}

/** Merr override-t e roleve bazë nga backend-i (burimi i vërtetë), me fallback te cache-i. */
export async function listRoleOverrides() {
  try {
    const r = await fetch(`${API}/api/roleOverrides`, {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    });
    if (r.ok) {
      const json = await r.json();
      const map = json?.data && typeof json.data === 'object' ? json.data : {};
      safeWrite(LS_ROLE_OVERRIDES, map); // pasqyro në cache
      return map;
    }
  } catch { /* offline */ }
  return loadRoleOverrides();
}

export async function renameRole(roleKey, newLabel) {
  const overrides = loadRoleOverrides();
  overrides[roleKey] = { ...(overrides[roleKey] || {}), label: newLabel };
  safeWrite(LS_ROLE_OVERRIDES, overrides);
  try {
    await fetch(`${API}/api/roleOverrides/${encodeURIComponent(roleKey)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ label: newLabel }),
    });
  } catch { /* offline */ }
  appendAudit({ kind: 'role_renamed', roleKey, roleLabel: newLabel });
  return { ok: true };
}

export async function resetRoleOverride(roleKey) {
  const overrides = loadRoleOverrides();
  if (overrides[roleKey]) {
    delete overrides[roleKey];
    safeWrite(LS_ROLE_OVERRIDES, overrides);
    appendAudit({ kind: 'role_reset', roleKey, roleLabel: roleKey });
  }
  try {
    await fetch(`${API}/api/roleOverrides/${encodeURIComponent(roleKey)}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });
  } catch { /* offline */ }
}

/* ──────────────────────────────────────────────────────────────────────────
 * Custom roles (backend `/api/customRoles`)
 * ────────────────────────────────────────────────────────────────────────── */
export async function listCustomRoles() {
  try {
    const r = await fetch(`${API}/api/customRoles`, {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    });
    if (!r.ok) return [];
    const json = await r.json();
    return Array.isArray(json?.data) ? json.data : [];
  } catch { return []; }
}

export async function createCustomRole({ name, description, color, permissions }) {
  const r = await fetch(`${API}/api/customRoles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({
      name,
      description: description || '',
      color: color || '#3D8FA3',
      scope: 'company',
      permissions: permissions ? JSON.stringify(permissions) : '[]',
    }),
  });
  const json = await r.json();
  if (!r.ok || !json?.success) {
    throw new Error(json?.error || 'Krijimi i rolit dështoi');
  }
  appendAudit({
    kind: 'role_created',
    roleKey: `custom:${json.data.id}`,
    roleLabel: name,
  });
  return json.data;
}

export async function updateCustomRole(id, { name, description, color, permissions }) {
  const r = await fetch(`${API}/api/customRoles/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({
      name,
      description: description || '',
      color: color || '#3D8FA3',
      scope: 'company',
      permissions: permissions ? JSON.stringify(permissions) : '[]',
    }),
  });
  const json = await r.json();
  if (!r.ok || !json?.success) {
    throw new Error(json?.error || 'Përditësimi i rolit dështoi');
  }
  appendAudit({
    kind: 'role_updated',
    roleKey: `custom:${id}`,
    roleLabel: name,
  });
  return json;
}

export async function deleteCustomRole(id) {
  const r = await fetch(`${API}/api/customRoles/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
  });
  if (!r.ok) throw new Error('Fshirja dështoi');
  appendAudit({ kind: 'role_deleted', roleKey: `custom:${id}`, roleLabel: `custom:${id}` });
  return { ok: true };
}

/* ──────────────────────────────────────────────────────────────────────────
 * Audit log
 * ────────────────────────────────────────────────────────────────────────── */
export function getAuditLog() {
  return safeRead(LS_AUDIT_LOG, []);
}

export function appendAudit(entry) {
  const log = getAuditLog();
  log.unshift({
    id: Date.now() + '-' + Math.random().toString(36).slice(2, 8),
    at: new Date().toISOString(),
    actor: getCurrentUserName(),
    ...entry,
  });
  while (log.length > MAX_AUDIT_ENTRIES) log.pop();
  safeWrite(LS_AUDIT_LOG, log);
}

export function clearAuditLog() {
  safeWrite(LS_AUDIT_LOG, []);
}

/* ──────────────────────────────────────────────────────────────────────────
 * User permission overrides (per përdorues individual)
 * ────────────────────────────────────────────────────────────────────────── */
/**
 * Përjashtimet për një përdorues — nga SERVERI, jo nga shfletuesi.
 *
 * Deri tani ruheshin vetëm te `localStorage`: paneli thoshte «u ruajtën», dhe
 * ato nuk arrinin kurrë te `users.permissions`. Një administrator tjetër, një
 * pajisje tjetër, ose vetë përdoruesi nuk shihnin asnjë ndryshim. Backend-i e
 * pret pikërisht këtë formë — `{ grant, deny }`.
 */
export async function loadUserOverrides(userId) {
  if (!userId) return { add: [], remove: [] };
  try {
    const r = await fetch(`${API}/api/users/${encodeURIComponent(userId)}`, { headers: getAuthHeaders() });
    const j = await r.json().catch(() => ({}));
    const raw = j?.data?.permissions ?? j?.data?.Permissions;
    const p = typeof raw === 'string' ? JSON.parse(raw || 'null') : raw;
    if (p && !Array.isArray(p) && (p.grant || p.deny)) {
      return { add: p.grant || [], remove: p.deny || [] };
    }
    if (Array.isArray(p)) return { add: p, remove: [] };
    if (p) return { add: [], remove: [] };      // matricë nivelesh — redaktohet te ekrani i saj
  } catch { /* offline → bie te cache-i lokal */ }
  const all = safeRead(LS_USER_OVERRIDES, {});
  return all[String(userId)] || { add: [], remove: [] };
}

export async function saveUserOverrides(userId, overrides) {
  if (!userId) throw new Error('User ID kërkohet');
  const all = safeRead(LS_USER_OVERRIDES, {});
  all[String(userId)] = {
    add:    Array.isArray(overrides.add)    ? [...new Set(overrides.add)]    : [],
    remove: Array.isArray(overrides.remove) ? [...new Set(overrides.remove)] : [],
    updatedAt: new Date().toISOString(),
  };
  safeWrite(LS_USER_OVERRIDES, all);

  const r = await fetch(`${API}/api/users/${encodeURIComponent(userId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ Permissions: { grant: all[String(userId)].add, deny: all[String(userId)].remove } }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j?.success === false) throw new Error(j?.error || `Ruajtja te serveri dështoi (HTTP ${r.status})`);
  appendAudit({
    kind: 'user_overrides_saved',
    userId: String(userId),
    addCount: all[String(userId)].add.length,
    removeCount: all[String(userId)].remove.length,
  });
  return { ok: true };
}
